import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Shield, ShieldCheck, ShieldAlert, Copy, Check, RefreshCw, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TwoFAPanel() {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState("idle"); // idle | qr | success | disable
  const [qrUrl, setQrUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/2fa/status`, { withCredentials: true });
      setStatus(data);
    } catch (err) {
      console.debug("[2fa] status fetch failed:", err?.response?.status);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startSetup = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/2fa/setup`, {}, { withCredentials: true });
      setQrUrl(data.qr_code_data_url);
      setSecret(data.secret);
      setStep("qr");
    } catch (err) {
      setError(err?.response?.data?.detail || "Setup fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const verifySetup = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/2fa/verify-setup`, { code }, { withCredentials: true });
      setBackupCodes(data.backup_codes);
      setStep("success");
      setCode("");
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Code ungültig");
    } finally { setBusy(false); }
  };

  const doDisable = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/auth/2fa/disable`, { password, code: code || undefined }, { withCredentials: true });
      setStep("idle"); setPassword(""); setCode("");
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Deaktivierung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const regenBackup = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/2fa/regenerate-backup`, {}, { withCredentials: true });
      setBackupCodes(data.backup_codes);
      setStep("success");
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Konnte nicht generieren");
    } finally { setBusy(false); }
  };

  const copyCodes = async () => {
    await navigator.clipboard.writeText(backupCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const enabled = status?.enabled;
  const mandatory = status?.mandatory;

  return (
    <div className="relative bg-gradient-to-br from-white/[0.015] to-transparent border border-white/[0.07] p-6 sm:p-7" data-testid="twofa-panel">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          {enabled ? <ShieldCheck size={16} className="text-[#00e5ff]" strokeWidth={1.8} /> : <Shield size={16} className="text-white/50" strokeWidth={1.6} />}
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#00e5ff]/80 font-outfit font-semibold">Sicherheit · 2FA</span>
        </div>
        <span className="text-[11px] font-outfit tracking-widest text-white/15">04</span>
      </div>

      <h2 className="font-outfit font-bold text-xl text-white tracking-tight mb-2">Zwei-Faktor-Authentifizierung</h2>
      <p className="text-sm text-white/50 mb-5 leading-relaxed">
        {mandatory
          ? "Als Administrator ist 2FA verpflichtend. Du kannst sie nicht deaktivieren."
          : "Optionaler zusätzlicher Schutz für deinen Account über eine Authenticator-App (TOTP)."}
      </p>

      {/* Status Badge */}
      <div className={`mb-5 p-4 border-l-4 flex items-start gap-3 ${enabled ? "border-[#00e5ff] bg-[#00e5ff]/[0.06]" : mandatory ? "border-yellow-400 bg-yellow-400/[0.06]" : "border-white/20 bg-white/[0.02]"}`}>
        {enabled ? (
          <>
            <ShieldCheck size={18} className="text-[#00e5ff] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white font-outfit font-semibold text-sm">2FA ist aktiv</p>
              <p className="text-white/60 text-xs mt-0.5">Eingerichtet am {status?.enrolled_at ? new Date(status.enrolled_at).toLocaleDateString("de-DE") : "—"} · {status?.remaining_backup_codes} Backup-Codes übrig</p>
            </div>
          </>
        ) : mandatory ? (
          <>
            <ShieldAlert size={18} className="text-yellow-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white font-outfit font-semibold text-sm">2FA erforderlich</p>
              <p className="text-white/70 text-xs mt-0.5">Als Admin musst du 2FA aktivieren, bevor du volle Rechte nutzen kannst.</p>
            </div>
          </>
        ) : (
          <>
            <Shield size={18} className="text-white/50 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white font-outfit font-semibold text-sm">2FA nicht aktiv</p>
              <p className="text-white/60 text-xs mt-0.5">Aktiviere 2FA für einen zusätzlichen Schutz deines Accounts.</p>
            </div>
          </>
        )}
      </div>

      {error && <div className="mb-4 text-red-400 text-sm bg-red-400/10 border border-red-400/30 px-3 py-2.5" data-testid="twofa-error">{error}</div>}

      {/* ACTIONS */}
      {step === "idle" && !enabled && (
        <Button
          onClick={startSetup}
          disabled={busy}
          data-testid="twofa-enable-btn"
          className="bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-none font-outfit font-semibold h-11 px-6 text-sm tracking-wide transition-all"
        >
          {busy ? "..." : "2FA aktivieren"}
        </Button>
      )}

      {step === "idle" && enabled && (
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={regenBackup}
            disabled={busy}
            variant="ghost"
            data-testid="twofa-regen-btn"
            className="border border-white/15 hover:border-[#00e5ff]/50 hover:text-[#00e5ff] text-white/80 rounded-none font-outfit font-medium h-11 px-5 text-sm"
          >
            <RefreshCw size={14} className="mr-2" />
            Backup-Codes neu generieren
          </Button>
          {!mandatory && (
            <Button
              onClick={() => { setStep("disable"); setError(""); }}
              variant="ghost"
              data-testid="twofa-disable-btn"
              className="border border-red-400/30 hover:border-red-400/70 hover:text-red-400 text-red-400/80 rounded-none font-outfit font-medium h-11 px-5 text-sm"
            >
              <X size={14} className="mr-2" />
              2FA deaktivieren
            </Button>
          )}
        </div>
      )}

      {/* QR SETUP */}
      {step === "qr" && (
        <div className="space-y-4">
          <div className="bg-black border border-white/10 p-4 text-center">
            <p className="text-[11px] uppercase tracking-[0.24em] text-[#00e5ff]/80 font-outfit font-semibold mb-3">1. QR-Code scannen</p>
            {qrUrl && <img src={qrUrl} alt="QR-Code für Authenticator" className="mx-auto w-56 h-56 bg-white p-2" data-testid="twofa-qr" />}
            <p className="text-[12px] text-white/50 mt-4 font-outfit">
              Scanne den Code mit deiner Authenticator-App (Google Authenticator, Authy, 1Password).
              Klappt's nicht? Trag den <b>manuellen Key</b> unten in die App ein.
            </p>
            <div className="mt-3 p-3 border border-white/10 bg-black/40 inline-block">
              <p className="text-[10px] uppercase tracking-[0.24em] text-white/50 font-outfit font-semibold mb-1">Manueller Key</p>
              <p className="text-[13px] text-[#00e5ff] break-all font-mono select-all" data-testid="twofa-secret">{secret}</p>
            </div>
          </div>

          <form onSubmit={verifySetup} className="space-y-3">
            <div>
              <Label className="text-white/50 text-[11px] uppercase tracking-[0.2em] font-outfit font-medium">2. 6-stelligen Code eingeben</Label>
              <Input
                autoFocus
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                data-testid="twofa-verify-input"
                className="mt-2 bg-black border-white/10 text-white rounded-none h-12 text-center tracking-[0.3em] font-mono text-lg focus:border-[#00e5ff]/60 focus:ring-0"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={busy || code.length !== 6}
                data-testid="twofa-verify-btn"
                className="flex-1 bg-[#00e5ff] text-black hover:bg-[#00e5ff] rounded-none font-outfit font-semibold h-11 text-sm"
              >
                {busy ? "..." : "Verifizieren & aktivieren"}
              </Button>
              <Button
                type="button"
                onClick={() => { setStep("idle"); setCode(""); setError(""); }}
                variant="ghost"
                className="border border-white/15 text-white/70 rounded-none h-11 px-4 text-sm"
              >
                Abbrechen
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* SUCCESS WITH BACKUP CODES */}
      {step === "success" && (
        <div className="space-y-4" data-testid="twofa-success">
          <div className="p-4 border border-[#00e5ff]/30 bg-[#00e5ff]/[0.05]">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={16} className="text-[#00e5ff]" />
              <span className="font-outfit font-semibold text-white text-sm">2FA erfolgreich aktiviert</span>
            </div>
            <p className="text-[13px] text-white/75 leading-relaxed">
              Speichere diese <span className="text-[#00e5ff] font-semibold">Backup-Codes</span> jetzt an einem sicheren Ort. Sie werden nur einmal angezeigt und retten dich, falls du den Zugriff auf deine Authenticator-App verlierst.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm">
            {backupCodes.map((c, i) => (
              <div key={c} className="bg-black border border-white/10 p-2.5 text-center text-white/85 tracking-wider" data-testid={`backup-code-${i}`}>
                {c}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={copyCodes}
              variant="ghost"
              data-testid="twofa-copy-backup"
              className="flex-1 border border-white/15 hover:border-[#00e5ff]/50 text-white/80 rounded-none h-10 font-outfit text-sm"
            >
              {copied ? <><Check size={14} className="mr-2" /> Kopiert!</> : <><Copy size={14} className="mr-2" /> Alle kopieren</>}
            </Button>
            <Button
              onClick={() => { setStep("idle"); setBackupCodes([]); }}
              className="bg-[#00e5ff] text-black hover:bg-[#00e5ff] rounded-none h-10 px-5 font-outfit font-semibold text-sm"
            >
              Fertig
            </Button>
          </div>
        </div>
      )}

      {/* DISABLE */}
      {step === "disable" && (
        <form onSubmit={doDisable} className="space-y-3" data-testid="twofa-disable-form">
          <p className="text-sm text-white/60">Bestätige deine Identität, um 2FA zu deaktivieren.</p>
          <div>
            <Label className="text-white/50 text-[11px] uppercase tracking-[0.2em] font-outfit font-medium">Passwort</Label>
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="twofa-disable-password"
              className="mt-2 bg-black border-white/10 text-white rounded-none h-11 focus:border-[#00e5ff]/60 focus:ring-0" />
          </div>
          <div>
            <Label className="text-white/50 text-[11px] uppercase tracking-[0.2em] font-outfit font-medium">2FA-Code (optional)</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="optional – 6 Ziffern" className="mt-2 bg-black border-white/10 text-white rounded-none h-11 focus:border-[#00e5ff]/60 focus:ring-0 font-mono tracking-widest text-center" />
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || !password} data-testid="twofa-disable-confirm"
              className="flex-1 bg-red-500/80 text-white hover:bg-red-500 rounded-none font-outfit font-semibold h-11 text-sm">
              {busy ? "..." : "2FA deaktivieren"}
            </Button>
            <Button type="button" onClick={() => { setStep("idle"); setPassword(""); setCode(""); setError(""); }}
              variant="ghost" className="border border-white/15 text-white/70 rounded-none h-11 px-4 text-sm">
              Abbrechen
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
